'use client';

import { Menu } from 'lucide-react';
import Link from 'next/link';
import { EnvironmentIndicator } from '@/components/common/EnvironmentIndicator';
import { StaticPageLayoutAuthHydrator } from '@/components/common/StaticPageLayoutAuthHydrator';
import { ThemeAwareLogo } from '@/components/common/ThemeAwareLogo';
import { Button, buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface MarketingHeaderProperties {
  envLabel?: string | undefined;
  envMode?: string | undefined;
}

const NAV_LINKS = [
  { href: '/features', label: 'Features' },
  { href: '/pricing', label: 'Pricing' },
  { href: '/contact', label: 'Contact' },
] as const;

const CTA_LOGIN_CLASS = cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'font-semibold');
const CTA_AUTH_CLASS = cn(buttonVariants({ variant: 'default', size: 'sm' }), 'font-semibold');
const CTA_LOGIN_LABEL = 'Login';
const CTA_AUTH_LABEL = 'Open Timeline';
const CTA_LOGIN_HREF = '/auth/signin';
const CTA_AUTH_HREF = '/timeline';
const MOBILE_CTA_CLASS =
  'block rounded-md px-2 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-secondary/40';

export function MarketingHeader({ envMode, envLabel }: MarketingHeaderProperties) {
  return (
    <header className="sticky top-0 z-layer-chrome w-full border-b border-border/60 bg-background/90 backdrop-blur-md supports-[backdrop-filter]:bg-card/80 shadow-sm">
      <div className="container mx-auto flex h-16 max-w-6xl items-center justify-between px-4">
        <Link href="/" className="flex items-center gap-2" aria-label="Sweetistics home">
          <ThemeAwareLogo alt="Sweetistics" width={32} height={32} className="h-8 w-8 object-contain" />
          <span className="text-lg font-semibold tracking-tight">Sweetistics</span>
        </Link>

        <nav className="flex flex-wrap items-center gap-6 max-sm:hidden" aria-label="Primary navigation">
          {NAV_LINKS.map(({ href, label }) => (
            <Link
              key={href}
              href={href}
              className="rounded-md px-2 py-1 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/35 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              {label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <EnvironmentIndicator mode={envMode} label={envLabel} />
          <Button asChild variant="outline" size="sm" className="font-semibold">
            <Link
              href={CTA_LOGIN_HREF}
              data-marketing-auth-cta
              data-login-class={CTA_LOGIN_CLASS}
              data-auth-class={CTA_AUTH_CLASS}
              data-auth-href={CTA_AUTH_HREF}
              data-auth-label={CTA_AUTH_LABEL}
              data-login-label={CTA_LOGIN_LABEL}
            >
              {CTA_LOGIN_LABEL}
            </Link>
          </Button>
          <details className="relative sm:hidden">
            <summary
              aria-label="Open navigation menu"
              className="flex h-9 w-9 cursor-pointer list-none items-center justify-center rounded-md border border-border/60 bg-card/80 transition-colors hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/35 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </summary>
            <div className="absolute right-0 mt-3 w-44 space-y-2 rounded-lg border border-border/60 bg-background/90 p-3 shadow-lg backdrop-blur-md supports-[backdrop-filter]:bg-card/80 dark:supports-[backdrop-filter]:bg-background/60">
              {NAV_LINKS.map(({ href, label }) => (
                <Link
                  key={href}
                  href={href}
                  className="block rounded-md px-2 py-1 text-sm font-medium text-foreground transition-colors hover:bg-secondary/40"
                >
                  {label}
                </Link>
              ))}
              <Link
                href={CTA_LOGIN_HREF}
                className={MOBILE_CTA_CLASS}
                data-marketing-auth-mobile-cta
                data-login-class={MOBILE_CTA_CLASS}
                data-auth-class={MOBILE_CTA_CLASS}
                data-auth-href={CTA_AUTH_HREF}
                data-auth-label={CTA_AUTH_LABEL}
                data-login-label={CTA_LOGIN_LABEL}
              >
                {CTA_LOGIN_LABEL}
              </Link>
            </div>
          </details>
          <StaticPageLayoutAuthHydrator />
        </div>
      </div>
    </header>
  );
}
